import { createContext, useContext, useState } from 'react';
import { useAuth } from './auth';
import { useCurrency } from './currency';
import { SearchService } from 'core/api/services/search';
import { isValidPositiveInteger } from '@utriper/core';
import { useDebounce } from '@utriper/core-ui';

const SearchContext = createContext(null);

export const ITEM_TYPE = {
    EXPERIENCE: 1,
    BUNDLE: 2,
};

const parseExperience = (experience) => {
    const experienceDetailSelected = experience?.experienceDetails?.find((expDetail) => expDetail.id === experience.experienceDetailId);
    const units = experienceDetailSelected?.units?.length > 0 ? experienceDetailSelected.units : experience?.units;

    return {
        ...experience,
        experienceId: experience?.experienceId || experience?.id,
        type: ITEM_TYPE.EXPERIENCE,
        experienceDetailSelected,
        units: units || [],
        defaultUnits: experience?.units ?? [],
        experiencePricing: experience?.pricing,
        pricing: {
            payTotal: null,
            payNow: null,
            currency: null,
            unitPricing: [],
        },
    };
};

const parseBundle = (bundle) => {
    const bundleExperiences = bundle?.bundleExperiences?.map((bundleExperience) => ({
        ...bundleExperience,
        defaultUnits: bundleExperience?.units ?? [],
        experiencePricing: bundleExperience?.pricing,
        pricing: {
            payTotal: null,
            payNow: null,
            currency: null,
            unitPricing: [],
        },
    }));

    return {
        ...bundle,
        bundleExperiences,
        type: ITEM_TYPE.BUNDLE,
    };
};

export const SearchProvider = ({ children }) => {
    const { userInfo, getUserInfoSync } = useAuth();
    const { currency } = useCurrency();
    const [searchValue, setSearchValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [experiences, setExperiences] = useState([]);
    const [experienceCursor, setExperienceCursor] = useState(null);
    const [bundleCursor, setBundleCursor] = useState(null);

    const searchFilterDebounce = useDebounce(searchValue, 300);

    const mergeResult = (experiences = [], bundles = []) => {
        const experiencesWithType = experiences.map(parseExperience);
        const bundlesWithType = bundles.map(parseBundle);

        const merged = [...experiencesWithType, ...bundlesWithType];

        merged.sort((a, b) => {
            return a?.id - b?.id;
        });

        return merged;
    };

    const search = async (contractId, experienceCursor, bundleCursor) => {
        try {
            if (loading) return null;

            setLoading(true);
            let experiencerId = userInfo?.experiencer?.id;

            if (!isValidPositiveInteger(experiencerId)) {
                const user = await getUserInfoSync();
                experiencerId = user?.experiencer?.id;
            }

            const options = { name: searchFilterDebounce, contractId, experiencerId, targetCurrency: currency || null };

            const [experienceResult, bundleResult] = await Promise.all([SearchService.searchExperiences({ ...options, cursor: experienceCursor }), SearchService.searchBundles({ ...options, cursor: bundleCursor })]);

            const { experiences, nextCursor: expCursor } = experienceResult;
            const { bundles, nextCursor: bdlCursor } = bundleResult;

            const result = mergeResult(experiences, bundles);
            if (!Array.isArray(result)) {
                return false;
            }

            if (isValidPositiveInteger(experienceCursor) || isValidPositiveInteger(bundleCursor)) {
                setExperiences((prevExperiences) => prevExperiences.concat(result));
            } else {
                setExperiences(result);
                setExperienceCursor(expCursor);
                setBundleCursor(bdlCursor);
            }

            return { experienceCursor: expCursor, bundleCursor: bdlCursor };
        } catch (error) {
            return false;
        } finally {
            setLoading(false);
        }
    };

    return (
        <SearchContext.Provider
            value={{
                search,
                searchFilterDebounce,
                setSearchValue,
                loading,
                experiences,

                // CURSOR PAGINATION
                experienceCursor,
                setExperienceCursor,
                bundleCursor,
                setBundleCursor,
            }}
        >
            {children}
        </SearchContext.Provider>
    );
};

SearchProvider.displayName = 'SearchProvider';

export const useSearch = () => useContext(SearchContext);
